import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button } from "../components/ui";
import { artFor } from "../funnel/art";
import type { LandingNode } from "../funnel/flow";
import { useFunnel, useFunnelConfig } from "../funnel/useFunnel";
import { AUDIENCE_PRESENTATION, resolveAudience } from "../lib/audience";
import { eventOnce, funnelApi, initialSessionId } from "../lib/api";
import { commitLandingConsent } from "../funnel/consent";

/**
 * The hero. A cold visitor from a paid-social ad has never heard of Bonded, so
 * the funnel says what it is before it asks for anything.
 *
 * Audience-aware via `?app=for-couples | for-first-dates | for-friends`
 * (default `for-anyone`), mirroring the SEO landing pages — the pill, headline
 * and CTA all swap. The audience is also persisted onto the session so
 * conversions can be segmented by entry point.
 */
export function LandingScreen({ node }: { node: LandingNode }) {
	const { t } = useTranslation();
	const { advance } = useFunnel();
	const config = useFunnelConfig();
	const audience = resolveAudience();
	// Starts unticked and stays unticked unless the visitor acts. GDPR Art.
	// 4(11) and Recital 32: a pre-ticked box is not consent.
	const [researchOptIn, setResearchOptIn] = useState(false);
	const researchInputId = useId();

	useEffect(() => {
		void eventOnce(initialSessionId, "landing_viewed", {
			variant: audience,
		}).catch(() => {});
	}, [audience]);

	const start = () => {
		// Continuing IS the agreement to the Terms. The research decision travels
		// with it but is a separate record — including when it is a refusal, so
		// that "asked and declined" is provable. Recorded first — every later
		// write chains off it — but the tap never waits on the round-trip.
		void commitLandingConsent(researchOptIn).catch(() => {});
		void funnelApi
			.event(initialSessionId, "agreement_accepted", {})
			.catch(() => {});
		void funnelApi
			.event(initialSessionId, "cta_clicked", { variant: audience })
			.catch(() => {});
		advance(node.id);
	};

	// Waitlist mode promises early access; paid mode promises the app itself.
	const cta =
		config.mode === "waitlist" ? t("landing.ctaWaitlist") : t("landing.cta");
	// Each audience gets its own brand illustration (AUDIENCE_PRESENTATION), so
	// the variants differ visually and not just in wording. Falls back to the
	// node's own art if an audience ever has none.
	const art =
		artFor(AUDIENCE_PRESENTATION[audience].heroArt) ?? artFor(node.art);

	return (
		<div className="content-column text-center landing-column">
			<Badge color="var(--cloud)" rotate={-2}>
				{t(`audiences.${audience}.pill`)}
			</Badge>
			<h1 className="display-title">{t(`audiences.${audience}.title`)}</h1>
			<p className="hero-sub">{t(`audiences.${audience}.sub`)}</p>
			{art ? <img src={art} alt="" className="hero-art" /> : null}
			<Button block size="lg" variant="sunshine" onClick={start}>
				{cta}
			</Button>
			<p className="hero-note">{t("landing.note")}</p>
			{/*
			  Research participation is optional and must be asked for separately
			  from the Terms — GDPR Art. 7(2), EDPB Guidelines 05/2020. Placed
			  below the CTA on purpose: it is genuinely optional, the funnel
			  behaves identically either way, and putting it above the button
			  would imply it is a step on the way through.
			*/}
			<label className="research-consent" htmlFor={researchInputId}>
				<input
					id={researchInputId}
					type="checkbox"
					checked={researchOptIn}
					onChange={(e) => setResearchOptIn(e.target.checked)}
				/>
				<span>{t("landing.researchOptIn")}</span>
			</label>
			<p className="legal-copy">
				{t("landing.agreementPrefix")}{" "}
				<a
					href="https://bonded.studio/terms-of-service/"
					target="_blank"
					rel="noreferrer noopener"
				>
					{t("landing.terms")}
				</a>{" "}
				{t("landing.and")}{" "}
				<a
					href="https://bonded.studio/privacy-policy/"
					target="_blank"
					rel="noreferrer noopener"
				>
					{t("landing.privacy")}
				</a>
				.
			</p>
		</div>
	);
}
